import React, { Fragment, useContext, useEffect } from 'react';
import { bool } from 'prop-types';
import classNames from 'classnames';
import {
  extend,
  params,
  string,
  useDataModel,
  arrayOf,
  shape,
  bool as boolType,
  number,
} from '@thd-nucleus/data-sources';
import { useStoreId } from '@thd-nucleus/experience-context';
import { Col, Image, Alert } from '@thd-olt-component-react/core-ui';
import { Tooltip } from '@thd-olt-component-react/tooltip';
import { OrderAPackageContext, determineSummaryDisplayPrice } from '../../OrderAPackageContext';
import { ProductImage } from '../subcomponents/productImage/ProductImage';
import { ProductDescription } from '../subcomponents/productDescription/ProductDescription';
import { TotalPricing } from '../subcomponents/totalPricing/TotalPricing';
import { SignInLink } from '../subcomponents/signInLink/SignInLink';
import { MessaggingSummary } from '../subcomponents/messaggingSummary/MessaggingSummary';

import { AddToCartButton } from '../subcomponents/addToCartButton/AddToCartButton';

import { getSummaryAvailabilityInfo, getQuantityError } from '../fulfillmentUtils';

import {
  FREE_SHIPPING_THRESHOLD, OUT_OF_STOCK_DETAILS, MAP_TOOLTIP_CONTENT
} from '../constants';
import { useBold } from '../../hooks/useBold/useBold';

import './summary.scss';

export const Summary = ({ card }) => {
  const {
    state, dispatch, actions, fulfillmentLoaded, isCustomerIdentified
  } = useContext(OrderAPackageContext);
  const {
    metadata,
    totalPricing,
    isMajorAppliancePackage,
    selectedProductIds,
    fetchPromotionalAdjustments,
    products: productsMap
  } = state;
  const parentClassNames = classNames('summary', { card });

  const { storeId } = useStoreId();

  const opts = {
    variables: {
      itemIds: selectedProductIds,
      storeId,
      isBrandPricingPolicyCompliant: isCustomerIdentified
    },
    ssr: false,
    skip: storeId === '8119' || !fetchPromotionalAdjustments
  };

  const { loading, data, error } = useDataModel('products', opts);
  const productsFulfillmentInfo = Array.from(productsMap?.values() || {});
  const hasMapPolicyProduct = productsFulfillmentInfo.some((product) => {
    const { pricing } = product;
    const { mapAboveOriginalPrice } = pricing || {};
    return mapAboveOriginalPrice !== null || pricing?.mapDetail?.mapPolicy !== null;
  });
  const specialPriceIfAuthProduct = productsFulfillmentInfo.some((product) => {
    const { pricing } = product;
    return pricing?.mapDetail?.mapPolicy === 'showSpecialPriceIfAuth';
  });
  const {
    unfulfillableItemIds,
  } = getSummaryAvailabilityInfo(productsMap, selectedProductIds);
  useEffect(() => {
    if (error) {
      dispatch(actions.promotionalAdjustmentsFetched({}));
    } else if (data && !loading) {
      dispatch(actions.promotionalAdjustmentsFetched(data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, loading, error]);

  const { boldRefs } = useBold(/\d+ in stock| \d+(?= on )/g);

  return (
    <Col className={parentClassNames}>
      <Col>
        <div className="summary__header">
          { metadata?.image?.url && (
            <div className="summary__header__package-image">
              <Image
                stretchy
                src={metadata?.image?.url.replace(/<SIZE>/g, '400')}
                alt="package image"
                title={`${metadata?.name} package image`}
                width={1}
                height={1}
              />
            </div>
          )}{/* eslint-disable-line */}
          <h2 className="summary__header__title">Your Bundle</h2>
        </div>
      </Col>
      {/* eslint-disable */}
      { selectedProductIds.length &&
        <Col className="summary__items-container" nopadding>
          { (productsFulfillmentInfo).filter( (item) => !unfulfillableItemIds.includes(item.itemId)).map((product, index) => {
              const { images } = product?.media;
              const { pricing, selected, productGroupSelected, selectedQty } = product;
              const { canonicalUrl, productLabel, brandName } = product?.identifiers;
              const {mapAboveOriginalPrice, value: priceValue} = pricing;
              const summaryDisplayPrice = determineSummaryDisplayPrice({ item: product, selectedQty });
              const summaryPrice = summaryDisplayPrice? summaryDisplayPrice : priceValue
              const isMappolicy = mapAboveOriginalPrice !== null || pricing?.mapDetail?.mapPolicy !== null;
              const isNewMapPolicy = pricing?.mapDetail?.mapPolicy === 'showSpecialPriceIfAuth';
              return ( selected && productGroupSelected &&
                <Col key={index} className="summary__item">
                  <Col md="3" lg="3" className="summary__item__image" nopadding>
                    <ProductImage
                      canonicalUrl={canonicalUrl}
                      productLabel={productLabel}
                      brandName={brandName}
                      images={images}
                      summary
                      disableLink
                    />
                  </Col>
                  <Col md="6" lg="6" nopadding>
                    <ProductDescription product={product} disableLink/>
                    <span>Qty: { product?.selectedQty || 1 }</span>
                  </Col>
                  <Col md="3" lg="3" className={"summary__item__price "} nopadding>
                  { summaryPrice && (
                    <span className={(isMappolicy && !isNewMapPolicy) ? "summary__item__strike" : ''}>
                      { `$${summaryPrice}` }
                    </span>
                  )}
                  {(isMappolicy) && (
                    !isNewMapPolicy ? (
                      <Tooltip
                        closeButton
                        content={MAP_TOOLTIP_CONTENT}
                        maxWidth={300}
                        placement="bottom"
                      >
                        <div style={{ color: '#3E7697', cursor: 'pointer', fontSize: '12px' }} id="summary-tooltip">
                          See Lower Price In Cart
                        </div>
                      </Tooltip>
                    ): !isCustomerIdentified && isNewMapPolicy && (
                      <SignInLink isOnListSummary/>
                    )
                  )}
                  </Col>
                </Col>
              );
            })
          }
        </Col>
      }
      <Col className="summary__cost-breakdown" nopadding>
        <TotalPricing value hideTotalPricing={hasMapPolicyProduct} haveAuthMapPolicy={specialPriceIfAuthProduct} title="Subtotal" />
        <TotalPricing bundleSavings title="Bundle Savings" />
        <TotalPricing additionalSavings title="Additional Savings" />
        { isMajorAppliancePackage &&
          <Col className="summary__cost-breakdown__delivery-message">
            {totalPricing.originalValueRaw >= FREE_SHIPPING_THRESHOLD ?
              <Fragment>
                <span className="delivery-message__title">Appliance Delivery</span>
                <span className="u__text--success">FREE</span>
              </Fragment>
              :
              <span className="delivery-message__title--below-threshold u__text--success">
                Free delivery on appliance purchases of $396 and up
              </span>
            }
          </Col>
        }{/* eslint-enable */}
        <Col className="summary__cost-breakdown__taxes">
          <div>
            <span>Taxes</span>
            <br />
            <span>(Calculated in checkout)</span>
          </div>
          <span>---</span>
        </Col>
      </Col>
      <Col className="summary__final-costs" nopadding>
        <TotalPricing
          totalValue
          hideTotalPricing={hasMapPolicyProduct}
          haveAuthMapPolicy={specialPriceIfAuthProduct}
          title="Total"
        />
        <TotalPricing totalSavings title="You'll Save" />
      </Col>
      <Col className="summary__add-to-cart">
        {[...productsMap.values()].some((product) => product.productGroupSelected && product.isOutOfStock) && (
          <div className="summary__out-of-stock">
            <Alert
              inline
              type="danger"
              title="Out of stock:"
              message={OUT_OF_STOCK_DETAILS}
            >
              <span
                className="summary__out-of-stock-link"
                onClick={() => dispatch(actions.removeOutOfStockItems())}
                role="button"
                tabIndex={0}
              >
                click here to remove them.
              </span>
            </Alert>
          </div>
        )}
        <div className="summary__quantity-errors">
          {[...productsMap.values()]
            .filter((product) => product.fulfillment &&
          product.productGroupSelected &&
          product.selected &&
          !product.isOutOfStock &&
          getQuantityError(fulfillmentLoaded, product, true))
            .map((product, i) => (
              <div
                className="summary__quantity-error"
                key={product.identifiers.itemId}
                ref={(el) => { boldRefs.current[i] = el; }}
              >
                <Alert
                  inline
                  type="warning"
                  message={getQuantityError(fulfillmentLoaded, product, true)}
                />
              </div>
            ))}
        </div>
        <MessaggingSummary
          mapPolicy={hasMapPolicyProduct}
          isCustomerIdentified={isCustomerIdentified}
          haveSpecialPriceIfAuthProduct={specialPriceIfAuthProduct}
        />
        <AddToCartButton />
      </Col>
    </Col>
  );
};

const ProductModel = extend({
  itemId: string(),
  dataSources: string(),
  fulfillment: shape({
    anchorStoreStatus: boolType(),
    anchorStoreStatusType: string(),
    backordered: boolType(),
    backorderedShipDate: string(),
    bossExcludedShipStates: string(),
    excludedShipStates: string(),
    fulfillmentOptions: shape({
      fulfillable: boolType(),
      services: shape({
        deliveryCharge: string(),
        deliveryDates: shape({
          endDate: string(),
          startDate: string(),
        }),
        deliveryTimeline: string(),
        freeDeliveryThreshold: string(),
        totalCharge: string()
      }),
      type: string()
    }),
    inStoreAssemblyEligible: boolType(),
    onlineStoreStatus: boolType(),
    onlineStoreStatusType: string(),
    seasonStatusEligible: string(),
  }),
  pricing: params({ storeId: string(), isBrandPricingPolicyCompliant: boolType().isRequired() }).shape({
    promotionalAdjustments: shape({
      type: string(),
      description: shape({
        shortDesc: string(),
        longDesc: string()
      }),
      dollarOff: number({ float: true }),
      percentageOff: number({ float: true }),
      promoId: string(),
    }),
  }),
});

Summary.propTypes = {
  card: bool,
};

Summary.defaultProps = {
  card: false,
};

Summary.displayName = 'Summary';

Summary.dataModel = extend({
  products: params({
    itemIds: arrayOf(string().isRequired()).isRequired(),
  }).arrayOf(ProductModel),
});
